/**
 * @author Hiroki Moto
 * @description https://xd.adobe.com/spec/12b34f37-4bc4-45ed-7e71-dcd9a54fa641-6482/screen/4c9eade5-e80e-405f-8a47-7219df78591d/DropDowns/
 */

import React, { useEffect, useMemo, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import OutsideClickHandler from 'react-outside-click-handler';
import cx from 'classnames';
import any from 'ramda/src/any';
import append from 'ramda/src/append';
import equals from 'ramda/src/equals';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import includes from 'ramda/src/includes';
import isNil from 'ramda/src/isNil';
import join from 'ramda/src/join';
import slice from 'ramda/src/slice';
import split from 'ramda/src/split';
import * as Animations from '@components/@core/Animations';
import Avatar from '@components/@core/Avatar';
import Button from '@components/@core/AnimatedButton';
import CheckBox from '@components/@core/CheckBox';
import Icon from '@components/@core/Icon';
import Pagination from '@components/@core/Pagination';
import { SPECIALOPTIONS } from '~/services/config';
import { inPage } from '~/services/util';

const Dropdown = ({
    className = '',
    error = '',
    data = [],
    type = 'default',
    title = '',
    renderAnchor,
    noOptionLabel = 'No options',
    placeholder = 'Select',
    multi = false,
    selectable = true,
    disabled = false,
    disabledOptions = [],
    align = 'left',
    direction = 'horizontal',
    titleDirection = 'horizontal',
    width = 'auto',
    height = 'auto',
    clearIds = [],
    defaultIndexes = null,
    defaultIds = null,
    splitted = false,
    caret = 'down',
    icon = 'fas fa-ellipsis',
    iconColor = '#343f4b',
    iconSize = 10,
    returnBy = 'id',
    page = false,
    mountEvent = false,
    stopPropagation = false,
    dataCy = '',
    header,
    footer,
    forceValue,
    getId = (e) => e['id'],
    getValue = (e) => e['value'],
    onChange = () => {},
    onSelected = () => {},
    onClear = null,
    onClick = () => {},
}) => {
    const [selected, setSelected] = useState([]);
    const [originals, setOriginals] = useState([]);
    const [opened, setOpened] = useState(false);
    const [current, setCurrent] = useState(1);

    const handleSpecial = (ID) => {
        if (SPECIALOPTIONS.ALL === ID) {
            // User select All option
            const newData = filter((d) => getId(d) === ID, data);
            const newSelected = newData.map((item) => getId(item));
            const returnData = returnBy === 'id' ? newSelected : newData;
            onChange(returnData);
            setSelected([SPECIALOPTIONS.ALL]);
        } else if (SPECIALOPTIONS.LIST === ID) {
            // User select List option
            const newData = filter((d) => getId(d) === ID, data);
            const newSelected = newData.map((item) => getId(item));
            const returnData = returnBy === 'id' ? newSelected : newData;
            onChange(returnData);
            setSelected([SPECIALOPTIONS.LIST]);
        } else if (SPECIALOPTIONS.NONE === ID) {
            // User select None option
            onChange([]);
            setSelected([SPECIALOPTIONS.NONE]);
        }
        setOpened(false);
    };

    const handleClose = () => {
        if (opened) {
            setOpened(false);
            if (multi && !equals(selected, originals)) handleApply();
        }
    };

    const handleToggle = (e) => {
        e.stopPropagation();
        if (disabled || (!header && data.length === 0)) return;
        if (stopPropagation) e.stopPropagation();
        setOpened(!opened);
        onClick();
    };

    const handleChange = (e) => {
        const ID = getId(e);
        onSelected(ID);
        if (includes(ID, disabledOptions)) return;
        if (ID < 0 && SPECIALOPTIONS.SELFASSIGNED !== ID) {
            handleSpecial(ID);
            return;
        }

        let newSelected;
        let newData;
        if (multi && selected[0] > 0) {
            if (includes(ID, selected)) newSelected = filter((x) => x != ID, selected);
            else newSelected = append(ID, selected);
        } else {
            newSelected = [ID];
        }
        newSelected = filter((item) => !isNil(item), newSelected);
        newData = filter((item) => !isNil(item) && any((sl) => sl === getId(item))(newSelected), data);
        const returnData = returnBy === 'id' ? newSelected : newData;
        setSelected(newSelected);

        if (multi) return;

        onChange(returnData);
        setOpened(false);
    };

    const handleClear = () => {
        setSelected(clearIds);
        handleClose();
        if (onClear) {
            onClear();
        } else {
            const newData = filter((e) => includes(getId(e), clearIds), data);
            const returnData = returnBy === 'id' ? clearIds : newData;
            onChange(returnData);
        }
    };

    const handleApply = () => {
        if (opened) setOpened(false);
        const newData = filter((item) => any((sl) => sl === getId(item))(selected), data);
        const returnData = returnBy === 'id' ? selected : newData;
        onChange(returnData);
    };

    const handleRowClick = (data, checked) => (e) => {
        e.stopPropagation();
        handleChange(data, checked);
    };

    const renderItem = (data, idx) => {
        const active = includes(getId(data), selected) && selectable;
        const className = cx('core-dropdown-item', { active }, type === 'avatar' && 'mb-2');
        const label = getValue(data);
        const renderIcon = data?.renderIcon;

        if (getId(data) === null && getValue(data) === 'BLOCK') {
            if (splitted) return <div key="block" className="split-line" />;
            return;
        }

        return (
            <div className={className} key={`cd${idx}`} onClick={handleRowClick(data, true)}>
                {type === 'thumbnail' && <Avatar className="mr-2" />}
                {type === 'avatar' && <Avatar className="mr-2" url={data.avatar} name={data.name} />}
                {!!renderIcon && renderIcon()}
                <span className="dsl-b16 text-nowrap" data-cy={`dropdownItem${(label || '').toString().replace(/[^A-Z0-9]+/gi, '')}`}>
                    {label}
                </span>
                <span className="d-flex-1" />
                {type === 'checkbox' && <CheckBox className="ml-2" size="tiny" id={data.id} checked={active} onChange={(e) => handleChange(data, e.target.checked)} />}
            </div>
        );
    };

    useEffect(() => {
        if (!header && data.length === 0) return;

        let newSelected = [];
        let newData = [];

        defaultIds?.forEach((id) => {
            newData.push(find((d) => getId(d) === id, data));
        });

        if (!isNil(defaultIds) && newData.length === 0) newData = [data[0]];

        defaultIndexes?.forEach((idx) => {
            newData.push(data[idx]);
        });

        newData = filter((e) => !isNil(e), newData);

        newSelected = newData.map((e) => getId(e));
        if (forceValue) {
            const selectedOpt = data.find((d) => d.name === forceValue);
            if (selectedOpt) {
                setSelected([selectedOpt.id]);
            }
        } else {
            setSelected(newSelected);
        }
        setOriginals(newSelected);

        const returnData = returnBy === 'id' ? newSelected : newData;
        if (mountEvent && selected.length === 0) onChange(returnData);
    }, [data, forceValue]);

    useEffect(() => {
        if ((!header && data.length === 0) || isNil(defaultIds)) return;
        let newSelected = [];
        let newData = [];

        defaultIds.forEach((id) => {
            newData.push(find((d) => getId(d) === id, data));
        });
        if (newData.length === 0) newData = [data[0]];
        newData = filter((e) => !isNil(e), newData);
        newSelected = newData.map((e) => getId(e));
        setSelected(newSelected);
        setOriginals(newSelected);
    }, [defaultIds]);

    const text = useMemo(() => {
        if (!header && data.length === 0) return noOptionLabel;
        if (caret === 'dots-without-title') return '';
        if (selected.length === 0) return placeholder;
        if (selected.length === 1) return getValue(data.find((d) => getId(d) === selected[0]));
        const titles = slice(
            0,
            2,
            selected.map((sel) => getValue(find((d) => getId(d) === sel, data)))
        );
        return `${join(', ', titles)} ${selected.length > 2 ? '...' : ''}`;
    }, [selected, data]);

    const selectedData = useMemo(() => {
        if (selected.length > 0) return data.find((e) => getId(e) === selected[0]);
    }, [selected, data]);

    const container = cx('core-dropdown', className);
    const toggle = cx('core-dropdown-toggle', direction);
    const menu = cx('core-dropdown-menu', align);
    const label = cx('core-dropdown-label', error !== '' ? 'error' : '');
    const maxHeight = height === 'auto' ? null : { maxHeight: height, overflowY: 'scroll' };

    return (
        <OutsideClickHandler display="flex" onOutsideClick={handleClose}>
            <div data-cy={dataCy} className={container} style={{ width }}>
                <div className={toggle} onClick={handleToggle}>
                    {renderAnchor ? (
                        renderAnchor()
                    ) : (
                        <Fragment>
                            {!!title && selectable && <div className={`core-dropdown-title dsl-b12 ${'vertical' === direction ? 'mb-2' : 'mr-2'}`}>{title}</div>}
                            {!!text && selectable && (
                                <div className={label}>
                                    {titleDirection === 'horizontal' ? (
                                        <div
                                            className={cx('d-flex ai-center me-3', {
                                                'ml-2': direction === 'vertical',
                                                'text-nowrap': !multi,
                                            })}
                                        >
                                            {type === 'avatar' && selected?.length > 0 && <Avatar className="mr-2" url={selectedData?.avatar} name={selectedData?.name} />}
                                            <span className="dsl-b16 text-400">{text}</span>
                                        </div>
                                    ) : (
                                        split(', ', text).map((item, idx) => (
                                            <div
                                                className={cx('d-flex ai-center me-4', {
                                                    'ml-2': direction === 'vertical',
                                                    'text-nowrap': !multi,
                                                })}
                                                key={`${title}${idx}`}
                                            >
                                                {type === 'avatar' && selected?.length > 0 && <Avatar className="mr-2" url={selectedData?.avatar} name={selectedData?.name} />}
                                                <span className="dsl-b16">{item}</span>
                                            </div>
                                        ))
                                    )}
                                    {caret === 'down' && <Icon name="fas fa-caret-down caret" color={iconColor} size={iconSize} />}
                                    {caret === 'icon-with-title' && <Icon name={icon} color={iconColor} size={iconSize} />}
                                </div>
                            )}
                            {!text && caret === 'dots-without-title' && <Icon name={icon} color={iconColor} size={iconSize} />}
                            {!selectable && !!placeholder && <div className="core-dropdown-title text-400">{placeholder}</div>}
                        </Fragment>
                    )}
                    {error !== '' && <p className="dsl-r12 my-2">{error}</p>}
                </div>
                <Animations.Popup className={menu} enter={10} exit={0} opened={opened} style={maxHeight}>
                    <>
                        {header}
                        <div className="menu-content">{data.map((item, idx) => ((page && inPage(idx, current, 15)) || !page) && renderItem(item, idx))}</div>
                        {multi && (
                            <div className="d-h-end mb-1 mt-2">
                                <Button className="ml-auto" name="Clear" type="link" onClick={handleClear} />
                                <Button className="ml-2" name="Apply" onClick={handleApply} />
                            </div>
                        )}
                        {data.length > 15 && page && <Pagination dataCy="taskOpen" current={current} per={15} pers={[]} total={Math.ceil(data?.length / 15)} onChange={(e) => setCurrent(e)} />}
                        {footer}
                    </>
                </Animations.Popup>
            </div>
        </OutsideClickHandler>
    );
};

Dropdown.propTypes = {
    className: PropTypes.string,
    error: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.any),
    type: PropTypes.oneOf(['default', 'avatar', 'checkbox', 'thumbnail']),
    title: PropTypes.string,
    noOptionLabel: PropTypes.string,
    placeholder: PropTypes.string,
    multi: PropTypes.bool,
    selectable: PropTypes.bool,
    disabled: PropTypes.bool,
    disabledOptions: PropTypes.array,
    align: PropTypes.oneOf(['left', 'right']),
    direction: PropTypes.oneOf(['horizontal', 'vertical']),
    titleDirection: PropTypes.oneOf(['horizontal', 'vertical']),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    clearIds: PropTypes.array,
    defaultIndexes: PropTypes.array,
    defaultIds: PropTypes.array,
    splitted: PropTypes.bool,
    caret: PropTypes.oneOf(['none', 'down', 'icon-with-title', 'dots-without-title']),
    icon: PropTypes.string,
    iconColor: PropTypes.string,
    iconSize: PropTypes.number,
    returnBy: PropTypes.oneOf(['id', 'data']),
    page: PropTypes.bool,
    mountEvent: PropTypes.bool,
    stopPropagation: PropTypes.bool,
    header: PropTypes.node,
    footer: PropTypes.node,
    forceValue: PropTypes.string,
    getId: PropTypes.func,
    getValue: PropTypes.func,
    onChange: PropTypes.func,
    onSelected: PropTypes.func,
    onClear: PropTypes.func,
};

export default Dropdown;
